<template>
  <hel-module title="同期经营情况对比分析" :class="scope.clsPrefix">
    <div class="chart-container">
      <div class="chart-subtitle">
        {{ lastYear }} vs {{ currentYear }} · 1-{{ months.length }}月同期
      </div>
      <div id="businessCompareChart" class="chart-body"></div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from './common/module/index.vue'
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'BusinessCompareAnalysis',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-business-compare'
      },
      months: [],
      lastYear: '',
      currentYear: '',
      amountUnit: '亿元',
      grossMarginUnit: '%',
      lastYearIncome: [],
      thisYearIncome: [],
      lastYearCost: [],
      thisYearCost: [],
      lastYearProfit: [],
      thisYearProfit: [],
      lastYearMargin: [],
      thisYearMargin: [],
      chartInstance: null
    }
  },
  mounted() {
    this.fetchBusinessSituation()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    async fetchBusinessSituation() {
      try {
        const response = await axios.post(
          'https://bigdata-cloud.cosmoplat.com/prod-api/system/api/call/erp/businessOperationSituation',
          {
            secret: '9k0NVP'
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        console.log('同期经营情况对比分析接口返回:', response.data)

        if (
          response.data.code === 200 &&
          Array.isArray(response.data.data) &&
          response.data.data.length > 0
        ) {
          // 按统计月份升序排列
          const sortedList = [...response.data.data].sort(
            (a, b) => Number(a.statMonth) - Number(b.statMonth)
          )

          this.months = sortedList.map((item) => `${item.statMonth}月`)
          this.lastYear = sortedList[0].lastYear
          this.currentYear = sortedList[0].currentYear
          this.amountUnit = sortedList[0].amountUnit || '亿元'
          this.grossMarginUnit = sortedList[0].grossMarginUnit || '%'

          // 字段映射层
          this.lastYearIncome = sortedList.map((i) =>
            Number(i.lastYearRevenue)
          )
          this.thisYearIncome = sortedList.map((i) =>
            Number(i.currentYearRevenue)
          )
          this.lastYearCost = sortedList.map((i) => Number(i.lastYearCost))
          this.thisYearCost = sortedList.map((i) =>
            Number(i.currentYearCost)
          )
          this.lastYearProfit = sortedList.map((i) =>
            Number(i.lastYearProfit)
          )
          this.thisYearProfit = sortedList.map((i) =>
            Number(i.currentYearProfit)
          )
          this.lastYearMargin = sortedList.map((i) =>
            Number(i.lastYearGrossMargin)
          )
          this.thisYearMargin = sortedList.map((i) =>
            Number(i.currentYearGrossMargin)
          )

          this.$nextTick(() => {
            this.drawChart()
          })
        }
      } catch (error) {
        console.error('获取同期经营情况对比分析数据失败:', error)
      }
    },
    drawChart() {
      const chart = echarts.init(
        document.getElementById('businessCompareChart')
      )
      const option = {
        tooltip: {
          trigger: 'axis',
          // 悬浮框约束在图表容器内：靠近上/左/右边缘时自动调整位置，
          // 保证标题行和 8 项指标内容始终完整展示，不被卡片边界裁切
          confine: true,
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: [
            '去年收入',
            '今年收入',
            '去年成本',
            '今年成本',
            '去年利润',
            '今年利润',
            '去年毛利率',
            '今年毛利率'
          ],
          top: 0,
          left: 'center',
          itemWidth: 14,
          itemHeight: 8,
          textStyle: {
            fontSize: 12,
            color: '#666666'
          }
        },
        grid: {
          top: 50,
          left: 10,
          right: 10,
          bottom: 30,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.months,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#999999',
            fontSize: 12
          }
        },
        yAxis: [
          {
            type: 'value',
            name: `金额（${this.amountUnit}）`,
            nameTextStyle: {
              color: '#999999',
              fontSize: 12,
              padding: [0, 0, 0, 30]
            },
            min: 0,
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            splitLine: {
              lineStyle: {
                color: '#f0f0f0',
                type: 'dashed'
              }
            },
            axisLabel: {
              color: '#999999',
              fontSize: 12,
              formatter: `{value}${this.amountUnit === '亿元' ? '亿' : ''}`
            }
          },
          {
            type: 'value',
            name: `毛利率（${this.grossMarginUnit}）`,
            nameTextStyle: {
              color: '#999999',
              fontSize: 12,
              padding: [0, 30, 0, 0]
            },
            min: 0,
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            splitLine: {
              show: false
            },
            axisLabel: {
              color: '#999999',
              fontSize: 12,
              formatter: `{value}${this.grossMarginUnit}`
            }
          }
        ],
        series: [
          {
            name: '去年收入',
            type: 'bar',
            barWidth: 10,
            itemStyle: {
              color: '#B8C4E8',
              borderRadius: [2, 2, 0, 0]
            },
            data: this.lastYearIncome
          },
          {
            name: '今年收入',
            type: 'bar',
            barWidth: 10,
            itemStyle: {
              color: '#4A64F4',
              borderRadius: [2, 2, 0, 0]
            },
            data: this.thisYearIncome
          },
          {
            name: '去年成本',
            type: 'bar',
            barWidth: 10,
            itemStyle: {
              color: '#A8E0C8',
              borderRadius: [2, 2, 0, 0]
            },
            data: this.lastYearCost
          },
          {
            name: '今年成本',
            type: 'bar',
            barWidth: 10,
            itemStyle: {
              color: '#36CFC9',
              borderRadius: [2, 2, 0, 0]
            },
            data: this.thisYearCost
          },
          {
            name: '去年利润',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 0,
            itemStyle: {
              color: '#7A63FE'
            },
            lineStyle: {
              color: '#7A63FE',
              width: 2,
              type: 'dashed'
            },
            data: this.lastYearProfit
          },
          {
            name: '今年利润',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 0,
            itemStyle: {
              color: '#7A63FE'
            },
            lineStyle: {
              color: '#7A63FE',
              width: 2,
              type: 'solid'
            },
            data: this.thisYearProfit
          },
          {
            name: '去年毛利率',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 1,
            itemStyle: {
              color: '#FA8C16'
            },
            lineStyle: {
              color: '#FA8C16',
              width: 2,
              type: 'dashed'
            },
            data: this.lastYearMargin
          },
          {
            name: '今年毛利率',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 1,
            itemStyle: {
              color: '#FA8C16'
            },
            lineStyle: {
              color: '#FA8C16',
              width: 2,
              type: 'solid'
            },
            data: this.thisYearMargin
          }
        ]
      }
      chart.setOption(option)
      this.chartInstance = chart
      // 修复 flex 容器下初始化时高度为 0 导致图表不回显的问题
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.chartInstance && this.chartInstance.resize()
        })
        setTimeout(() => {
          this.chartInstance && this.chartInstance.resize()
        }, 200)
      })
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    }
  }
}
</script>

<style lang="scss">
$class-prefix: "rvc-cosmoplat-workspace-business-compare";
.#{$class-prefix} {
  .chart-container {
    height: 100%;
    min-height: 320px;
    display: flex;
    flex-direction: column;
    padding: 10px 0;
    box-sizing: border-box;
  }
  .chart-subtitle {
    align-self: flex-end;
    font-family: PingFangSC-Regular;
    font-size: 12px;
    color: #999999;
    font-weight: 400;
    margin-bottom: 4px;
  }
  .chart-body {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 280px;
  }
}
</style>
